import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import emailjs from "emailjs-com";

function Contact() {
	const [formData, setFormData] = useState({
		from_name: "",
		reply_email: "",
		message: ""
	});
	const [focused, setFocused] = useState(null);
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});

	const validateForm = () => {
		const newErrors = {};
		const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
		if (!formData.from_name.trim()) newErrors.from_name = true;
		if (!formData.reply_email || !emailRegex.test(formData.reply_email))
			newErrors.reply_email = true;
		if (!formData.message.trim()) newErrors.message = true;
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!validateForm()) {
			toast.error("Please fill in all fields correctly");
			return;
		}
		setLoading(true);
		try {
			await emailjs.send(
				process.env.REACT_APP_EMAILJS_SERVICE_ID,
				process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
				{
					from_name: formData.from_name,
					reply_email: formData.reply_email,
					message: formData.message
				},
				process.env.REACT_APP_EMAILJS_USER_ID
			);
			toast.success("Message sent successfully! I'll get back to you soon.", {
				position: "top-center",
				autoClose: 3000
			});
			setFormData({ from_name: "", reply_email: "", message: "" });
			setErrors({});
		} catch (error) {
			toast.error(
				"Failed to send message. Please try again or email me directly."
			);
		} finally {
			setLoading(false);
		}
	};

	const contactInfo = [
		{
			icon: "📧",
			label: "Email",
			value: "matteorobidoux@gmail.com",
			link: "mailto:matteorobidoux@gmail.com"
		},
		{
			icon: "💼",
			label: "LinkedIn",
			value: "Connect with me",
			link: "https://www.linkedin.com/in/matteo-robidoux-736062264/"
		},
		{
			icon: "💻",
			label: "GitHub",
			value: "@matteorobidoux",
			link: "https://github.com/matteorobidoux"
		}
	];

	return (
		<div className="min-h-screen px-4 sm:px-6 lg:px-8 py-24">
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				className="max-w-4xl mx-auto"
			>
				<h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 text-center text-white">
					Get In Touch
				</h1>
				<p className="text-gray-400 text-center mb-12 sm:mb-16 text-base sm:text-lg px-4">
					Have an opportunity or project in mind? Let's connect.
				</p>

				<div className="grid md:grid-cols-2 gap-8 sm:gap-10 lg:gap-12">
					<motion.div
						initial={{ opacity: 0, x: -50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ delay: 0.2 }}
					>
						<h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">
							Let's Connect
						</h2>
						<p className="text-gray-300 text-sm sm:text-base mb-6 sm:mb-8">
							I'm always interested in hearing about new projects and
							opportunities.
						</p>
						<div className="space-y-4 sm:space-y-6">
							{contactInfo.map((item, index) => (
								<motion.a
									key={item.label}
									href={item.link}
									target="_blank"
									rel="noopener noreferrer"
									initial={{ opacity: 0, x: -20 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ delay: 0.4 + index * 0.1 }}
									className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white/5 rounded-lg border border-white/10 hover:border-white/30 transition-all cursor-pointer group"
								>
									<span className="text-2xl sm:text-3xl">{item.icon}</span>
									<div>
										<p className="text-gray-400 text-xs sm:text-sm">
											{item.label}
										</p>
										<p className="text-white font-medium text-sm sm:text-base group-hover:text-gray-300 transition-colors">
											{item.value}
										</p>
									</div>
								</motion.a>
							))}
						</div>
					</motion.div>

					<motion.form
						initial={{ opacity: 0, x: 50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ delay: 0.4 }}
						onSubmit={handleSubmit}
						className="space-y-4 sm:space-y-6"
					>
						{[
							{ field: "from_name", label: "Name", type: "text" },
							{ field: "reply_email", label: "Email", type: "email" }
						].map(({ field, label, type }) => (
							<div key={field}>
								<label className="block text-white font-medium mb-2 text-sm sm:text-base">
									{label}
								</label>
								<input
									type={type}
									name={field}
									className={`w-full px-3 py-2 sm:px-4 sm:py-3 bg-white/5 border ${
										errors[field]
											? "border-red-500"
											: focused === field
												? "border-white/50"
												: "border-white/10"
									} rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-white/50 transition-colors`}
									onFocus={() => setFocused(field)}
									onBlur={() => setFocused(null)}
									value={formData[field]}
									onChange={(e) => {
										setFormData({ ...formData, [field]: e.target.value });
										setErrors({ ...errors, [field]: false });
									}}
								/>
							</div>
						))}

						<div>
							<label className="block text-white font-medium mb-2 text-sm sm:text-base">
								Message
							</label>
							<textarea
								rows={5}
								name="message"
								className={`w-full px-3 py-2 sm:px-4 sm:py-3 bg-white/5 border ${
									errors.message
										? "border-red-500"
										: focused === "message"
											? "border-white/50"
											: "border-white/10"
								} rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-white/50 transition-colors resize-none`}
								onFocus={() => setFocused("message")}
								onBlur={() => setFocused(null)}
								value={formData.message}
								onChange={(e) => {
									setFormData({ ...formData, message: e.target.value });
									setErrors({ ...errors, message: false });
								}}
							/>
						</div>

						<motion.button
							whileHover={{ scale: loading ? 1 : 1.02 }}
							whileTap={{ scale: loading ? 1 : 0.98 }}
							type="submit"
							disabled={loading}
							className={`w-full py-3 sm:py-4 bg-white rounded-lg text-slate-900 text-sm sm:text-base font-semibold hover:bg-gray-100 hover:shadow-lg hover:shadow-white/20 transition-all duration-300 flex items-center justify-center gap-2 ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
						>
							{loading ? (
								<>
									<svg
										className="animate-spin h-5 w-5"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										></circle>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									Sending...
								</>
							) : (
								"Send Message"
							)}
						</motion.button>
					</motion.form>
				</div>
			</motion.div>
			<ToastContainer
				position="top-center"
				autoClose={3000}
				hideProgressBar={false}
				newestOnTop={false}
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
				theme="dark"
			/>
		</div>
	);
}

export default Contact;
